import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Calendar as CalendarIcon, 
  Plus, 
  MapPin, 
  Clock,
  Trash2,
  Download,
  ChevronRight,
  Users,
  UserPlus,
  UserMinus,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate iCal content
const generateICalContent = (event) => {
  const formatDate = (dateStr, timeStr) => {
    const date = new Date(dateStr);
    if (timeStr) {
      const [hours, minutes] = timeStr.split(':');
      date.setHours(parseInt(hours), parseInt(minutes), 0);
    }
    return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  };
  
  const startDate = formatDate(event.date, event.time);
  let endDate;
  if (event.time) {
    const endDateTime = new Date(event.date);
    const [hours, minutes] = event.time.split(':');
    endDateTime.setHours(parseInt(hours) + 2, parseInt(minutes), 0);
    endDate = endDateTime.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  } else {
    const nextDay = new Date(event.date);
    nextDay.setDate(nextDay.getDate() + 1);
    endDate = nextDay.toISOString().split('T')[0].replace(/-/g, '');
  }
  
  const uid = `${event.id}@shrt.ch`;
  const now = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  
  let description = event.lead || event.description || '';
  if (event.link) {
    description += (description ? '\\n\\n' : '') + 'Link: ' + event.link;
  }
  
  const icalContent = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//SHRT//Event Calendar//DE',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${now}`,
    event.time ? `DTSTART:${startDate}` : `DTSTART;VALUE=DATE:${event.date.replace(/-/g, '')}`,
    event.time ? `DTEND:${endDate}` : `DTEND;VALUE=DATE:${endDate}`,
    `SUMMARY:${event.title.replace(/,/g, '\\,')}`,
    description ? `DESCRIPTION:${description.replace(/\n/g, '\\n').replace(/,/g, '\\,')}` : '',
    event.location ? `LOCATION:${event.location.replace(/,/g, '\\,')}` : '',
    event.link ? `URL:${event.link}` : '',
    'END:VEVENT',
    'END:VCALENDAR'
  ].filter(line => line).join('\r\n');
  
  return icalContent;
};

const downloadICalFile = (event) => {
  const content = generateICalContent(event);
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.title.replace(/[^a-zA-Z0-9]/g, '_')}.ics`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const isUpcoming = (dateStr) => {
  const eventDate = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return eventDate >= today;
};

export default function EventsPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPast, setShowPast] = useState('');
  const [registeringEvent, setRegisteringEvent] = useState(null);

  const getToken = () => localStorage.getItem('shrt_token');

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.get(
        `${API_URL}/api/events`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEvents(response.data.events || []);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (e, eventId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm('Event wirklich löschen?')) return;
    const token = getToken();
    
    try {
      await axios.delete(
        `${API_URL}/api/events/${eventId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Event gelöscht');
      fetchEvents();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleRegister = async (e, event) => {
    e.preventDefault();
    e.stopPropagation();
    
    // If payment required or has max participants, go to detail page for full modal
    if (event.payment_required || event.max_participants) {
      navigate(`/events/${event.id}`);
      return;
    }
    
    // Simple registration for free events without limits
    setRegisteringEvent(event.id);
    const token = getToken();
    
    try {
      const response = await axios.post(
        `${API_URL}/api/events/${event.id}/register`,
        { guests: 0 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler bei der Anmeldung');
    } finally {
      setRegisteringEvent(null);
    }
  };

  const handleUnregister = async (e, eventId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!confirm('Anmeldung wirklich stornieren?')) return;
    setRegisteringEvent(eventId);
    const token = getToken();
    
    try {
      await axios.delete(
        `${API_URL}/api/events/${eventId}/register`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Anmeldung storniert');
      fetchEvents();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Stornieren');
    } finally {
      setRegisteringEvent(null);
    }
  };

  const upcomingEvents = events.filter(e => isUpcoming(e.date));
  const allPast = events.filter(e => !isUpcoming(e.date)).reverse();
  const pastEvents = allPast.filter(e => !e.has_review);
  const reviewEvents = allPast.filter(e => e.has_review);

  return (
    <Layout>
      <div className=" max-w-4xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div className="flex items-center gap-3">
            <CalendarIcon className="text-primary" size={28} />
            <h1 className="font-['Oswald'] text-2xl md:text-3xl font-bold text-foreground uppercase tracking-tight">
              Events
            </h1>
          </div>
          
          {isAdmin && (
            <Button
              onClick={() => navigate('/events/new')}
              className="bg-primary hover:bg-red-700 text-white uppercase tracking-widest font-bold text-sm rounded-none w-full sm:w-auto"
              data-testid="add-event-btn"
            >
              <Plus size={18} className="mr-2" />
              Neues Event
            </Button>
          )}
        </div>

        {/* Upcoming Events */}
        <div className="mb-12">
          <h2 className="font-['Oswald'] text-lg font-bold uppercase tracking-wider text-foreground mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-primary rounded-full"></span>
            Kommende Events
          </h2>
          
          {loading ? (
            <div className="text-muted-foreground">Laden...</div>
          ) : upcomingEvents.length === 0 ? (
            <div className="text-muted-foreground py-8 text-center bg-card border border-border">
              Keine kommenden Events
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingEvents.map(event => (
                <EventCard 
                  key={event.id} 
                  event={event} 
                  isAdmin={isAdmin}
                  onDelete={handleDelete}
                  onDownload={downloadICalFile}
                  onRegister={handleRegister}
                  onUnregister={handleUnregister}
                  registeringEvent={registeringEvent}
                />
              ))}
            </div>
          )}
        </div>

        {/* Rückblicke */}
        {reviewEvents.length > 0 && (
          <div className="mb-12">
            <button
              onClick={() => setShowPast(prev => prev === 'reviews' ? '' : 'reviews')}
              className="font-['Oswald'] text-lg font-bold uppercase tracking-wider text-foreground mb-4 flex items-center gap-2 hover:text-primary transition-colors"
              data-testid="toggle-review-events"
            >
              <span className="w-2 h-2 bg-primary rounded-full"></span>
              Rückblicke ({reviewEvents.length})
              <span className="text-sm ml-2">{showPast === 'reviews' ? '▲' : '▼'}</span>
            </button>
            
            {showPast === 'reviews' && (
              <div className="space-y-4">
                {reviewEvents.map(event => (
                  <EventCard 
                    key={event.id} 
                    event={event} 
                    isAdmin={isAdmin}
                    onDelete={handleDelete}
                    onDownload={downloadICalFile}
                    isPast
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Vergangene Events (ohne Rückblick) */}
        {pastEvents.length > 0 && (
          <div>
            <button
              onClick={() => setShowPast(prev => prev === 'past' ? '' : 'past')}
              className="font-['Oswald'] text-lg font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2 hover:text-foreground transition-colors"
              data-testid="toggle-past-events"
            >
              <span className="w-2 h-2 bg-muted-foreground rounded-full"></span>
              Vergangene Events ({pastEvents.length})
              <span className="text-sm ml-2">{showPast === 'past' ? '▲' : '▼'}</span>
            </button>
            
            {showPast === 'past' && (
              <div className="space-y-4 opacity-70">
                {pastEvents.map(event => (
                  <EventCard 
                    key={event.id} 
                    event={event} 
                    isAdmin={isAdmin}
                    onDelete={handleDelete}
                    onDownload={downloadICalFile}
                    isPast
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

// Event Card Component
function EventCard({ event, isAdmin, onDelete, onDownload, onRegister, onUnregister, registeringEvent, isPast = false }) {
  const stats = event.stats || {};
  const myReg = event.my_registration;
  const isRegistered = !!myReg;
  const isWaitlist = myReg?.status === 'waitlist';
  const maxParticipants = event.max_participants;
  const isFull = maxParticipants && stats.total_participants >= maxParticipants;
  const isRegistrationOpen = event.registration_open !== false;
  const isLoading = registeringEvent === event.id;

  return (
    <Link 
      to={`/events/${event.id}`}
      className={`block bg-card border border-border overflow-hidden ${isPast ? '' : 'border-l-4 border-l-primary'} hover:bg-muted/30 transition-colors group`}
      data-testid={`event-card-${event.id}`}
    >
      <div className="flex">
        {/* Event Image - 4:3 Format */}
        {(event.image || event.image_url) && (
          <div className="w-40 sm:w-48 flex-shrink-0" style={{ aspectRatio: '4/3' }}>
            <img 
              src={event.image || event.image_url}
              alt={event.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        <div className="flex-1 p-4 sm:p-5 flex flex-col gap-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            {/* Date Badge */}
            <div className="flex-shrink-0 flex sm:flex-col items-center sm:items-start gap-2 sm:gap-0 sm:w-20 sm:text-left">
              <div className={`text-2xl sm:text-3xl font-bold ${isPast ? 'text-muted-foreground' : 'text-primary'}`}>
                {new Date(event.date).getDate()}
              </div>
              <div className="text-sm text-muted-foreground uppercase">
                {new Date(event.date).toLocaleDateString('de-CH', { weekday: 'short' })}, {new Date(event.date).toLocaleDateString('de-CH', { month: 'short', year: 'numeric' })}
              </div>
            </div>

            {/* Event Details */}
            <div className="flex-1 min-w-0">
              <h3 className={`font-semibold text-lg ${isPast ? 'text-muted-foreground' : 'text-foreground'} group-hover:text-primary transition-colors`}>
                {event.title}
              </h3>
              
              <div className="flex flex-wrap gap-3 mt-2 text-sm text-muted-foreground">
                {event.time && (
                  <span className="flex items-center gap-1">
                    <Clock size={14} /> {event.time} Uhr
                  </span>
                )}
                {event.location && (
                  <span className="flex items-center gap-1">
                    <MapPin size={14} /> {event.location}
                  </span>
                )}
                {maxParticipants && (
                  <span className="flex items-center gap-1">
                    <Users size={14} /> 
                    {stats.total_participants || 0}/{maxParticipants}
                    {stats.total_waitlist > 0 && ` (+${stats.total_waitlist} Warteliste)`}
                  </span>
                )}
              </div>
              
              {(event.lead || event.description) && (
                <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
                {event.lead || event.description}
              </p>
            )}

            {/* Registration Status Badge */}
            {!isPast && isRegistered && (
              <div className={`inline-flex items-center gap-1 mt-3 px-2 py-1 text-xs uppercase tracking-wider ${
                isWaitlist 
                  ? 'bg-yellow-500/20 text-yellow-600' 
                  : 'bg-green-500/20 text-green-600'
              }`}>
                {isWaitlist ? (
                  <><AlertCircle size={12} /> Warteliste</>
                ) : (
                  <><CheckCircle size={12} /> Angemeldet</>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {!isPast && isRegistrationOpen && (
              <>
                {isRegistered ? (
                  <Button
                    onClick={(e) => onUnregister?.(e, event.id)}
                    variant="outline"
                    size="sm"
                    className="text-xs gap-1 h-8 rounded-none border-destructive text-destructive hover:bg-destructive hover:text-white"
                    disabled={isLoading}
                    data-testid={`unregister-${event.id}`}
                  >
                    {isLoading ? <Loader2 size={14} className="animate-spin" /> : <UserMinus size={14} />}
                    <span className="hidden sm:inline">Abmelden</span>
                  </Button>
                ) : (
                  <Button
                    onClick={(e) => onRegister?.(e, event)}
                    variant={isFull ? "outline" : "default"}
                    size="sm"
                    className={`text-xs gap-1 h-8 rounded-none ${isFull ? 'border-yellow-500 text-yellow-600 hover:bg-yellow-500 hover:text-black' : ''}`}
                    disabled={isLoading}
                    data-testid={`register-${event.id}`}
                  >
                    {isLoading ? <Loader2 size={14} className="animate-spin" /> : <UserPlus size={14} />}
                    <span className="hidden sm:inline">{isFull ? 'Warteliste' : 'Anmelden'}</span>
                  </Button>
                )}
              </>
            )}
            
            <Button
              onClick={(e) => { e.preventDefault(); onDownload(event); }}
              variant="ghost"
              size="sm"
              className="text-xs gap-1 h-8"
              data-testid={`download-ical-${event.id}`}
            >
              <Download size={14} />
            </Button>
            
            {isAdmin && (
              <button
                onClick={(e) => onDelete(e, event.id)}
                className="p-2 hover:text-destructive transition-colors"
                data-testid={`delete-event-${event.id}`}
              >
                <Trash2 size={16} />
              </button>
            )}
            
            <ChevronRight size={20} className="text-muted-foreground group-hover:text-primary transition-colors hidden sm:block" />
          </div>
        </div>
        </div>
      </div>
    </Link>
  );
}
